import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CredentialResponse } from '@react-oauth/google';
import { setAuthRefreshHandler, setAuthTokenProvider } from '../api/client';
import { requestSilentGoogleCredential } from './googleSilentRefresh';
import { decodeJwtPayload, getTokenExpiryMs, isTokenExpired } from './tokenUtils';

type AuthContextValue = {
  idToken: string | null;
  userEmail: string | null;
  isAuthenticated: boolean;
  authRequired: boolean;
  setCredential: (response: CredentialResponse) => void;
  signOut: () => void;
  getAccessToken: () => string | null;
  refreshAccessToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const ALLOWED_HD = process.env.REACT_APP_ALLOWED_GOOGLE_HD || 'shinkei.systems';
/** Refresh this long before JWT exp so in-flight requests still succeed. */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const SESSION_STORAGE_KEY = 'dataviewer_google_id_token';

function readStoredToken(): string | null {
  try {
    const token = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!token || isTokenExpired(token)) {
      if (token) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

function persistToken(token: string | null): void {
  try {
    if (token) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, token);
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // private mode / quota — ignore
  }
}

function emailFromToken(token: string): string | null {
  const claims = decodeJwtPayload(token);
  return typeof claims?.email === 'string' ? claims.email : null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const initialToken = readStoredToken();
  const [idToken, setIdToken] = useState<string | null>(initialToken);
  const [userEmail, setUserEmail] = useState<string | null>(
    initialToken ? emailFromToken(initialToken) : null,
  );

  const idTokenRef = useRef<string | null>(idToken);
  const refreshInFlightRef = useRef<Promise<string | null> | null>(null);

  const authRequired = Boolean(GOOGLE_CLIENT_ID);

  const applyCredential = useCallback((token: string) => {
    setIdToken(token);
    setUserEmail(emailFromToken(token));
    persistToken(token);
  }, []);

  const setCredential = useCallback(
    (response: CredentialResponse) => {
      const token = response.credential;
      if (!token) {
        return;
      }
      applyCredential(token);
    },
    [applyCredential],
  );

  const signOut = useCallback(() => {
    setIdToken(null);
    setUserEmail(null);
    persistToken(null);
  }, []);

  const getAccessToken = useCallback(() => idTokenRef.current, []);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    if (!GOOGLE_CLIENT_ID) {
      return idTokenRef.current;
    }

    const current = idTokenRef.current;
    if (current && !isTokenExpired(current, REFRESH_MARGIN_MS)) {
      return current;
    }

    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const refreshPromise = (async () => {
      const response = await requestSilentGoogleCredential(GOOGLE_CLIENT_ID, {
        hostedDomain: ALLOWED_HD,
      });
      const token = response?.credential ?? null;
      if (token) {
        applyCredential(token);
        return token;
      }
      // Keep a still-valid token if silent refresh failed early.
      if (current && !isTokenExpired(current, 0)) {
        return current;
      }
      signOut();
      return null;
    })();

    refreshInFlightRef.current = refreshPromise;
    try {
      return await refreshPromise;
    } finally {
      if (refreshInFlightRef.current === refreshPromise) {
        refreshInFlightRef.current = null;
      }
    }
  }, [applyCredential, signOut]);

  useEffect(() => {
    idTokenRef.current = idToken;
  }, [idToken]);

  useEffect(() => {
    setAuthTokenProvider(() => idTokenRef.current);
  }, []);

  useEffect(() => {
    setAuthRefreshHandler(() => refreshAccessToken());
    return () => setAuthRefreshHandler(null);
  }, [refreshAccessToken]);

  // Proactively refresh before the Google ID token expires (~1h).
  useEffect(() => {
    if (!authRequired || !idToken) {
      return undefined;
    }

    const expiry = getTokenExpiryMs(idToken);
    if (expiry == null) {
      return undefined;
    }

    let cancelled = false;
    let timer: number | undefined;

    const schedule = (delayMs: number) => {
      timer = window.setTimeout(async () => {
        const token = await refreshAccessToken();
        if (cancelled || !token) {
          return;
        }
        // If silent refresh failed but the JWT is still valid, retry nearer to expiry.
        const nextExpiry = getTokenExpiryMs(token);
        if (nextExpiry == null) {
          return;
        }
        const msLeft = nextExpiry - Date.now();
        if (msLeft > 60_000 && isTokenExpired(token, REFRESH_MARGIN_MS)) {
          schedule(Math.min(60_000, Math.max(msLeft - 30_000, 5_000)));
        }
      }, delayMs);
    };

    schedule(Math.max(expiry - Date.now() - REFRESH_MARGIN_MS, 0));

    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [authRequired, idToken, refreshAccessToken]);

  // Refresh when the tab becomes visible again after being backgrounded.
  useEffect(() => {
    if (!authRequired) {
      return undefined;
    }

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      const token = idTokenRef.current;
      if (!token || isTokenExpired(token, REFRESH_MARGIN_MS)) {
        void refreshAccessToken();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [authRequired, refreshAccessToken]);

  const value = useMemo(
    () => ({
      idToken,
      userEmail,
      isAuthenticated: !authRequired || Boolean(idToken),
      authRequired,
      setCredential,
      signOut,
      getAccessToken,
      refreshAccessToken,
    }),
    [
      authRequired,
      getAccessToken,
      idToken,
      refreshAccessToken,
      setCredential,
      signOut,
      userEmail,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function getGoogleClientId(): string {
  return GOOGLE_CLIENT_ID;
}
